import {
  mapTransactionStatusToTag,
  type GetGenericTransactionResponseDtoOutput,
  type GetIncomingTransferResponseDto,
  type GetOutgoingTransferResponseDto,
} from '@peanuds/shared';

import type { TransactionDetailsDialogData } from '../transaction-details.types';

type IncomingTransferDetails = GetIncomingTransferResponseDto['data']['incomingTransfer'];
type IncomingTransferAccount = NonNullable<IncomingTransferDetails['senderAccount']>;
type OutgoingTransferDetails = GetOutgoingTransferResponseDto['data'];
type GenericTransferDetails = GetGenericTransactionResponseDtoOutput['data']['genericTransaction'];

const toOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;

  const normalized = String(value).trim();
  return normalized ? normalized : undefined;
};

const toDisplayPaymentType = (value: unknown): string | undefined => {
  const paymentType = toOptionalString(value);
  if (!paymentType) return undefined;

  return paymentType
    .split('-')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(' ');
};

const joinDefined = (values: Array<string | undefined>, separator = ', '): string | undefined => {
  const parts = values.filter((value): value is string => Boolean(value));
  return parts.length > 0 ? parts.join(separator) : undefined;
};

const toStatusMeta = (
  status: string | null | undefined,
): Pick<TransactionDetailsDialogData, 'statusLabelKey' | 'statusSeverity'> => {
  const statusTag = mapTransactionStatusToTag(status);

  return {
    statusLabelKey: statusTag.value,
    statusSeverity: statusTag.severity,
  };
};

const toIncomingAccountName = (
  account: IncomingTransferAccount | undefined,
): string | undefined => {
  if (!account) return undefined;

  return (
    toOptionalString(account.accountHolderName) ??
    toOptionalString(account.alias) ??
    toOptionalString(account.iban) ??
    toOptionalString(account.accountNumber)
  );
};

const toBeneficiaryName = (transfer: OutgoingTransferDetails): string | undefined => {
  const identity = transfer.beneficiary?.identity;

  return (
    toOptionalString(transfer.beneficiary?.fullName) ??
    joinDefined(
      [toOptionalString(identity?.givenName), toOptionalString(identity?.familyName)],
      ' ',
    ) ??
    toOptionalString(identity?.legalName) ??
    toOptionalString(transfer.beneficiary?.account?.accountHolderName) ??
    toOptionalString(transfer.beneficiary?.account?.iban) ??
    toOptionalString(transfer.beneficiary?.account?.accountNumber)
  );
};

const toOutgoingSummaryType = (transfer: OutgoingTransferDetails): string => {
  if (transfer.scope === 'external') return 'EXTERNAL TRANSFER';
  if (transfer.scope === 'internal') return 'INTERNAL TRANSFER';

  return transfer.destination?.type === 'beneficiary-bank-account'
    ? 'EXTERNAL TRANSFER'
    : 'INTERNAL TRANSFER';
};

const toIncomingSummaryType = (transfer: IncomingTransferDetails): string => {
  if (transfer.scope === 'external') return 'EXTERNAL TRANSFER';
  if (transfer.scope === 'internal') return 'INTERNAL TRANSFER';

  return 'INCOMING TRANSFER';
};

const genericTransferDirectionByType: Record<string, TransactionDetailsDialogData['direction']> = {
  'balance-deposit': 'in',
  'balance-change': 'in',
  'returned-incoming-reverse': 'in',
  'balance-withdrawal': 'out',
  'subscription-fee': 'out',
  'returned-incoming': 'out',
  'returned-outgoing': 'out',
};

const genericTransferDirectionByAttribute: Record<
  string,
  TransactionDetailsDialogData['direction']
> = {
  INBOUND: 'in',
  OUTBOUND: 'out',
};

const toGenericTransferDirection = (
  transfer: GenericTransferDetails,
  fallbackDirection: TransactionDetailsDialogData['direction'],
): TransactionDetailsDialogData['direction'] => {
  const typeDirection = transfer.type ? genericTransferDirectionByType[transfer.type] : undefined;
  if (typeDirection) return typeDirection;

  const attributeDirection = transfer.attributes?.direction;
  if (typeof attributeDirection !== 'string') return fallbackDirection;

  return genericTransferDirectionByAttribute[attributeDirection] ?? fallbackDirection;
};

const toOutgoingAddress = (transfer: OutgoingTransferDetails): string | undefined => {
  return joinDefined(
    [
      joinDefined(
        [
          toOptionalString(transfer.beneficiary?.address?.street),
          toOptionalString(transfer.beneficiary?.address?.houseNumber),
        ],
        ' ',
      ),
      toOptionalString(transfer.beneficiary?.address?.refinement),
      joinDefined(
        [
          toOptionalString(transfer.beneficiary?.address?.postalCode),
          toOptionalString(transfer.beneficiary?.address?.city),
        ],
        ' ',
      ),
      toOptionalString(transfer.beneficiary?.address?.region),
      toOptionalString(transfer.beneficiary?.address?.country),
    ],
    ', ',
  );
};

const toOutgoingBankNameAddress = (transfer: OutgoingTransferDetails): string | undefined => {
  return joinDefined([
    toOptionalString(transfer.beneficiary?.account?.bankName),
    toOutgoingAddress(transfer),
  ]);
};

const toIncomingBankNameAddress = (transfer: IncomingTransferDetails): string | undefined => {
  const bankName = toOptionalString(transfer.senderAccount?.bankName);
  if (!bankName) return undefined;

  return joinDefined([bankName, toOptionalString(transfer.senderAccount?.country)]);
};

const toInitiatedByName = (
  initiatedBy: { firstName?: string | null; lastName?: string | null } | null | undefined,
): string | undefined => {
  return joinDefined(
    [toOptionalString(initiatedBy?.firstName), toOptionalString(initiatedBy?.lastName)],
    ' ',
  );
};

export const mapOutgoingTransferDetailsToDialogData = (
  response: GetOutgoingTransferResponseDto,
): TransactionDetailsDialogData => {
  const transfer = response.data;
  const beneficiaryName = toBeneficiaryName(transfer);

  return {
    id: toOptionalString(transfer.id) ?? '',
    direction: 'out',
    transactionNumber: toOptionalString(transfer.transactionNumber),
    summaryName: beneficiaryName,
    summaryType: toOutgoingSummaryType(transfer),
    ...toStatusMeta(transfer.status),
    dateTime: toOptionalString(transfer.transferDate),
    originalTransferDate: toOptionalString(transfer.originalTransferDate),
    amount: transfer.amount ?? transfer.totalAmount ?? null,
    fee: transfer.feeAmount ?? null,
    currency:
      toOptionalString(transfer.currency) ??
      toOptionalString(transfer.beneficiary?.account?.currency),
    feeCurrency: toOptionalString(transfer.feeCurrency),
    fromAccountId: toOptionalString(transfer.source?.accountId),
    beneficiaryName,
    initiatedByName: toInitiatedByName(transfer.initiatedBy),
    bankNameAddress: toOutgoingBankNameAddress(transfer),
    reference: toOptionalString(transfer.description),
    scheme: transfer.scheme ?? undefined,
    scope: transfer.scope,
    returned: transfer.returned ?? null,
    outgoingTransferDetails: transfer,
  };
};

export const mapIncomingTransferDetailsToDialogData = (
  response: GetIncomingTransferResponseDto,
): TransactionDetailsDialogData => {
  const transfer = response.data.incomingTransfer;
  const senderName =
    toOptionalString(transfer.senderName) ?? toIncomingAccountName(transfer.senderAccount);
  const receiverName = toIncomingAccountName(transfer.receiverAccount);

  return {
    id: toOptionalString(transfer.id) ?? '',
    direction: 'in',
    transactionNumber: toOptionalString(transfer.transactionNumber),
    summaryName: senderName,
    summaryType: toIncomingSummaryType(transfer),
    ...toStatusMeta(transfer.status),
    dateTime: toOptionalString(transfer.transferDate),
    amount: transfer.amount ?? transfer.totalAmount ?? null,
    fee: transfer.feeAmount ?? null,
    currency:
      toOptionalString(transfer.currency) ??
      toOptionalString(transfer.receiverAccount?.currency) ??
      toOptionalString(transfer.senderAccount?.currency),
    feeCurrency: toOptionalString(transfer.feeCurrency),
    fromAccountName: senderName,
    fromAccountIban: toOptionalString(transfer.senderAccount?.iban),
    beneficiaryName: receiverName,
    bankNameAddress: toIncomingBankNameAddress(transfer),
    reference:
      toOptionalString(transfer.senderInformation) ??
      toOptionalString(transfer.serviceProviderReference) ??
      toOptionalString(transfer.transactionNumber),
    scheme: transfer.scheme ?? undefined,
    scope: transfer.scope,
    incomingTransferDetails: transfer,
  };
};

export const mapGenericTransferDetailsToDialogData = (
  response: GetGenericTransactionResponseDtoOutput,
  fallbackDirection: TransactionDetailsDialogData['direction'],
): TransactionDetailsDialogData => {
  const transfer = response.data.genericTransaction;
  const paymentType = toDisplayPaymentType(transfer.type);

  return {
    id: toOptionalString(transfer.id) ?? '',
    direction: toGenericTransferDirection(transfer, fallbackDirection),
    transactionNumber: toOptionalString(transfer.transactionNumber),
    summaryName: paymentType,
    summaryType: 'INTERNAL TRANSFER',
    ...toStatusMeta(transfer.status),
    dateTime: toOptionalString(transfer.creationDateTime),
    amount: transfer.amount ?? null,
    fee: null,
    currency: toOptionalString(transfer.currency),
    reference:
      toOptionalString(transfer.narrative) ??
      toOptionalString(transfer.transactionNumber) ??
      paymentType,
    scope: 'internal',
    beneficiaryName: paymentType,
    genericTransferDetails: transfer,
  };
};
